import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { NewBadge } from 'components/Pages/HotelSearchPage/HotelSearchFilterNewBadge'
import { objectEntries } from 'lib/object/objectUtils'

// [slug, count, order, label]
export type Option = [string, number, number, string]

function sortByCheckedAndOrder(a: Option, b: Option, checkedSet: Set<string>, overrideOptions?: Set<string>) {
  const checkedA = checkedSet.has(a[0])
  const checkedB = checkedSet.has(b[0])
  if (checkedA && !checkedB) {
    return -1
  } else if (!checkedA && checkedB) {
    return 1
  } else if (overrideOptions?.has(a[0]) && !overrideOptions.has(b[0])) {
    return -1
  } else if (overrideOptions && !overrideOptions.has(a[0]) && overrideOptions.has(b[0])) {
    return 1
  } else {
    return b[2] - a[2]
  }
}

function sortByNameAndOrder(options: Array<Option>) {
  return options.sort((a, b) => a[0].localeCompare(b[0]))
    .sort((a, b) => b[2] - a[2])
}

export function sortRecentItems(a: FilterPanelCheckItem, b: FilterPanelCheckItem, orderBy: Array<string | number>) {
  const checkedA = a.checked
  const checkedB = b.checked
  const indexA = orderBy.indexOf(a.value)
  const indexB = orderBy.indexOf(b.value)

  if (a.count === 0) {
    return -1
  } else if (b.count === 0) {
    return 1
  } else if (checkedA && !checkedB) {
    return -1
  } else if (!checkedA && checkedB) {
    return 1
  } else if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB
  } else if (indexA !== -1) {
    return -1
  } else if (indexB !== -1) {
    return 1
  } else {
    return (b.count ?? 0) - (a.count ?? 0)
  }
}

export function sortPopularItems(
  a: FilterPanelCheckItem,
  b: FilterPanelCheckItem,
  orderBy: Record<string, number>,
  overrideOptions?: Set<string | number>,
) {
  const checkedA = a.checked
  const checkedB = b.checked
  if (checkedA && !checkedB) {
    return -1
  } else if (!checkedA && checkedB) {
    return 1
  } else if (overrideOptions?.has(a.value) && !overrideOptions.has(b.value)) {
    return -1
  } else if (overrideOptions && !overrideOptions.has(a.value) && overrideOptions.has(b.value)) {
    return 1
  } else {
    return (orderBy[a.label as string] ?? 0) - (orderBy[b.label as string] ?? 0)
  }
}

export interface FilterOptionItems {
  label: React.ReactElement
  value: string | number
  checked: boolean
  count?: number
  hideCount?: boolean
}

export function generateOptionItems(
  options: Array<Option>,
  selectedSet: Set<string>,
  settings: {
    sortByChecked?: boolean,
    newOptions?: Set<string>,
    hideCounts?: boolean,
  } = {},
): Array<FilterOptionItems> {
  const { sortByChecked, newOptions } = settings
  const sortedOptions = sortByChecked ?
      options.sort((a, b) => sortByCheckedAndOrder(a, b, selectedSet, newOptions)) :
      sortByNameAndOrder(options)
  return sortedOptions.map(([slug, count, , label]) => ({
    label: <div>
      <span>{label}</span>
      {newOptions?.has(slug) &&
        <NewBadge><FormattedMessage
          id="Se_oNKFUGtQGD7wQAQLX"
          defaultMessage="NEW"
          
        />
        </NewBadge>}
    </div>,
    value: slug,
    count,
    checked: selectedSet.has(slug),
    hideCount: settings.hideCounts,
  }))
}

export function filterItemsToOptions(items: Array<App.FilterItem>, orders: Record<string, number> = {}): Array<Option> {
  return items.map(item => [item.slug, item.count, orders[item.slug] ?? 0, item.label])
}

export function hasNoAvailableFilters(availableFilters: App.OfferListAvailableFilters | undefined): boolean {
  if (!availableFilters) {
    return true
  }

  return objectEntries(availableFilters).every(([key, value]) => {
    if (key === 'total') {
      return value === 0
    }
    if (Array.isArray(value)) {
      return value.length === 0 || value.every((item: App.FilterItem) => item.count === 0)
    }
    if (typeof value === 'object') {
      return Object.values(value).every(val => val === 0)
    }
    return value === 0
  })
}
